import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ICommand } from '../../../core/commands/ICommand';
import { getCommandManager } from '../../../hooks/useCommandManager';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import { useDiagramStore } from '../../../stores/diagramStore';
import { useScenariosStore } from '../../../core/assistant/stores/scenariosStore';
import { buildPeopleHierarchy } from '../services/buildPeopleChartScene';
import { commitScenarioDelta, type CommitScenarioResult } from '../services/commitScenarioDelta';
import {
  ClientScenarioPreparationError, clientScenarioCompletion, prepareClientScenarioOperationalChange,
} from '../services/clientScenarioOperationalPreparation';
import type { OrgModelProjection } from '../models/OrgModelProjection';
import type { PositionStructureIntent } from '../models/PositionStructureIntent';
import type { PositionStaffingResult } from '../models/PositionStaffingIntent';
import type { OrgArtifactViewDefinitionLike } from '../models/orgArtifactKind';
import { legacyAtlasTransferId } from '../services/legacyAtlasDraftTransfer';
import { completeAtlasDraftSave, trackAtlasDraftSave, useAtlasDraftSaveRecoveryStore } from '../stores/atlasDraftSaveRecoveryStore';
import { useOrgPlanningScenarioStore } from '../stores/orgPlanningScenarioStore';
import { openAtlasChanges } from '../stores/atlasChangesStore';
import { useOrgModel } from './useOrgModel';
import { orgModelQueryKey } from './orgModelQueryKeys';
import { invalidateOrgPlanningQueries } from './invalidateOrgPlanningQueries';
import { captureOrgPlanningReviewScope, isOrgPlanningReviewScopeCurrent } from './useOrgPlanningReviewScope';
import type { OrgPlanningReviewScope } from './orgPlanningReviewKey';
import { subscribeProtectedPlanningInvalidation } from './protectedPlanningGeneration';
import { PositionCommandSettlementError, postPositionIntentWithReconciliation } from './positionCommandSettlement';

// Protected request closures stay in session memory, not the CRDT or query keys.
interface LegacyTransfer {
  scope: OrgPlanningReviewScope; revision: string; command: ICommand; model: OrgModelProjection;
  staged: boolean; versionConflict: boolean; result?: CommitScenarioResult;
}
const transfers = new Map<string, LegacyTransfer>();
subscribeProtectedPlanningInvalidation((folderId) => {
  for (const [key, transfer] of transfers) if (!folderId || transfer.scope.folderId === folderId) transfers.delete(key);
});

/** Explicit compatibility transfer. It saves the COMPLETE legacy delta once;
 * the single Changes host owns subsequent preparation, review, and Apply. */
export function useCommitScenario(input: {
  folderId: string | null | undefined;
  diagramId: string | null | undefined;
  viewDefinition: OrgArtifactViewDefinitionLike | null | undefined;
}) {
  const folderId = input.folderId ?? '';
  const diagramId = input.diagramId ?? '';
  const request = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const orgModel = useOrgModel({ folderId, diagramId, enabled: Boolean(folderId) });
  const attempts = useAtlasDraftSaveRecoveryStore((state) => state.attempts);
  const mutation = useMutation({ mutationFn: async (intent: PositionStructureIntent): Promise<PositionStaffingResult> => {
    const scope = captureOrgPlanningReviewScope(folderId, diagramId);
    const response = await postPositionIntentWithReconciliation(() => {
      if (!isOrgPlanningReviewScopeCurrent(scope)) throw new Error('Your access changed. Reopen the chart.');
      return request(`/api/folders/${folderId}/diagrams/${diagramId}/atlas/position-structure-intents`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(intent),
      });
    }, () => undefined);
    const result = await response.json().catch(() => null) as PositionStaffingResult & { error?: string } | null;
    if (!response.ok || !result) throw new PositionCommandSettlementError('http_error', response.status, result?.error);
    return result;
  } });
  const { mutateAsync: stageIntent } = mutation;
  const commit = useCallback(async (): Promise<CommitScenarioResult | null> => {
    const store = useScenariosStore.getState();
    const scenario = store.getActiveScenario();
    const scope = captureOrgPlanningReviewScope(folderId, diagramId);
    const requireCurrentScope = () => {
      if (!isOrgPlanningReviewScopeCurrent(scope)) throw new Error('Your access changed. Reopen the chart.');
    };
    requireCurrentScope();
    if (!scenario || store.diagramId !== diagramId || !folderId || !diagramId || input.viewDefinition === undefined) return null;
    if (scenario.submissionState === 'submitted' && scenario.submittedScenarioId && scenario.submittedChangeSetId) {
      return { status: scenario.submittedOperationalChangeSetId ? 'prepared' : 'staged', appliedCount: 0, unsupported: [],
        scenarioId: scenario.submittedScenarioId, changeSetId: scenario.submittedChangeSetId,
        operationalChangeSetId: scenario.submittedOperationalChangeSetId };
    }
    const revision = scenario.updatedAt.toISOString();
    const completion = clientScenarioCompletion(input.viewDefinition);
    const key = JSON.stringify([scope, scenario.id]);
    let transfer = transfers.get(key);
    if (transfer && transfer.revision !== revision) {
      throw new Error('This legacy draft changed during its transfer. Review the saved change and the remaining local edits separately; they have not been merged.');
    }
    if (!transfer) {
      const model = orgModel.data;
      if (!model?.completeness.complete) throw new Error('Wait for the complete organization to load before transferring this draft.');
      if (useOrgPlanningScenarioStore.getState().selectedByDiagram[diagramId]) {
        throw new Error('A server draft is already selected. Review that draft before transferring this separate legacy draft.');
      }
      const other = Object.values(useAtlasDraftSaveRecoveryStore.getState().attempts).find((attempt) =>
        attempt.scope.folderId === folderId && attempt.scope.diagramId === diagramId && isOrgPlanningReviewScopeCurrent(attempt.scope));
      if (other) throw new Error('Resolve the unsaved edit before transferring this legacy draft.');
      const nodes = new Map(useDiagramStore.getState().nodes.map((node) => [node.id, node]));
      const intentId = legacyAtlasTransferId(folderId, diagramId, scenario.id, revision);
      const entry: LegacyTransfer = { scope, revision, command: null!, model, staged: false, versionConflict: false };
      const execute = async () => {
        requireCurrentScope();
        trackAtlasDraftSave(intentId, scope, entry.command, 'saving');
        // A definite version conflict before any intent was staged leaves nothing
        // bound to the old version, so the retry stages against the refetched model.
        const latest = queryClient.getQueryData<OrgModelProjection>(orgModelQueryKey({ folderId, diagramId }));
        if (entry.versionConflict && latest?.completeness.complete && latest.graphVersion > entry.model.graphVersion) {
          entry.model = latest;
        }
        entry.versionConflict = false;
        const base = entry.model;
        const byPerson = buildPeopleHierarchy(base).positionByPersonId;
        const positions = new Map(base.positions.map((position) => [position.id, position]));
        const people = new Set(base.people.map((person) => person.id));
        try {
          let intentIndex = 0;
          entry.result = await commitScenarioDelta({
            delta: scenario.delta,
            recordIdByNodeId: (id) => {
              const node = nodes.get(id);
              const attrs = node?.attrs ?? {};
              const binding = (node?.props?.dataBinding ?? attrs.dataBinding) as { recordId?: string } | undefined;
              const value = attrs.folderRecordId ?? binding?.recordId ?? attrs.personId ?? attrs.externalId;
              return typeof value === 'string' ? value : undefined;
            },
            positionByRecordId: (id) => {
              const position = positions.get(id) ?? byPerson.get(id);
              return position ? { id: position.id, title: position.label } : undefined;
            },
            isPersonRecord: (id) => people.has(id),
            ownerDiagramId: diagramId,
            baseGraphVersion: base.graphVersion,
            effectiveAt: base.asOf,
            scenarioName: 'Atlas canvas edits',
            newIntentId: () => legacyAtlasTransferId(folderId, diagramId, scenario.id, `${revision}:${intentIndex++}`),
            stageIntent: async (intent) => {
              requireCurrentScope();
              const receipt = await stageIntent(intent);
              if (receipt.intentId !== intent.intentId || receipt.outcome !== 'staged'
                || !receipt.changeSetId || !receipt.scenarioId || !Number.isFinite(receipt.graphVersion)
                || receipt.supersededChangeSetId !== (intent.scenarioChangeSetId ?? null)) {
                throw new Error('The draft save could not be confirmed. Retry this same transfer before making another change.');
              }
              entry.staged = true;
              return {
                changeSetId: receipt.changeSetId,
                scenarioId: receipt.scenarioId,
                graphVersion: receipt.graphVersion,
              };
            },
            completion,
            prepareOperationalChangeSet: completion === 'prepare_operational'
              ? (changeSetId) => prepareClientScenarioOperationalChange({
                authenticatedFetch: request, folderId, diagramId, changeSetId,
              }).catch((error: unknown) => {
                // A plan needing a distinct approver is saved; its named review owns the hand-off.
                if (error instanceof ClientScenarioPreparationError && error.outcome === 'not_prepared'
                  && error.details.code === 'approval_required') return null;
                throw error;
              })
              : undefined,
          });
          requireCurrentScope();
          if (entry.result.status === 'nothing_to_commit' || !entry.result.changeSetId || !entry.result.scenarioId) {
            completeAtlasDraftSave(intentId);
            return;
          }
          const current = useScenariosStore.getState();
          // Keep the overlay until exact server settlement. Never clear newer input.
          const linked = entry.result.unsupported.length === 0
            && current.diagramId === diagramId
            && current.markScenarioSubmitted(scenario.id, revision, {
              scenarioId: entry.result.scenarioId, changeSetId: entry.result.changeSetId,
              ...(entry.result.operationalChangeSetId
                ? { operationalChangeSetId: entry.result.operationalChangeSetId }
                : {}),
            });
          if (linked) transfers.delete(key);
          completeAtlasDraftSave(intentId);
          if (current.diagramId === diagramId) {
            openAtlasChanges(folderId, diagramId, entry.result.operationalChangeSetId ?? entry.result.changeSetId);
          }
          void invalidateOrgPlanningQueries(queryClient, folderId).catch(() => undefined);
        } catch (error) {
          const definite = (error instanceof PositionCommandSettlementError && error.status !== undefined
            && error.status >= 400 && error.status < 500 && error.serverCode !== 'intent_in_progress')
            || (error instanceof ClientScenarioPreparationError && error.outcome === 'not_prepared');
          if (error instanceof PositionCommandSettlementError && error.serverCode === 'graph_version_conflict') {
            entry.versionConflict = !entry.staged;
            await invalidateOrgPlanningQueries(queryClient, folderId).catch(() => undefined);
          }
          trackAtlasDraftSave(intentId, scope, entry.command, definite ? 'failed' : 'unknown');
          throw error;
        }
      };
      entry.command = { id: intentId, name: 'Transfer legacy organization draft', timestamp: Date.now(),
        skipHistory: true, mutationDestination: 'protected_plan', execute, redo: execute, undo: () => undefined };
      transfers.set(key, entry);
      transfer = entry;
    }
    await getCommandManager().execute(transfer.command);
    return transfer.result ?? null;
  }, [diagramId, folderId, input.viewDefinition, orgModel.data, queryClient, stageIntent]);
  return { commit, isCommitting: Object.values(attempts).some((attempt) => attempt.scope.folderId === folderId
    && attempt.scope.diagramId === diagramId && attempt.phase === 'saving' && isOrgPlanningReviewScopeCurrent(attempt.scope)) };
}
